import "./AnalyticsUnavailable.css";

import { Badge, Button } from "reactstrap";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  hasAnalyticsRole,
  hasAnovaRole,
  hasFeaturesRole,
  hasPcaRole,
} from "../users/rolesUtil";
import {
  selectDatesWithResults,
  selectSelectedTrialContractInfo,
} from "../selectors/resultMap";
import { useDispatch, useSelector } from "react-redux";

import { ControlSection } from "./components/ControlSection/ControlSection";
import { EmptyState } from "../components/EmptyState";
import { InlineSpinner } from "../components/InlineSpinner";
import { fetchResultMapFeatures } from "../actions/resultMap";
import { useMemo } from "react";

// Trial dates are ISO strings ("2026-08-12").
const formatDay = (date) => format(parseISO(date), "d MMM yyyy");

// Mirrors the role checks of AnalyticsLayout, which redirects here. Names as
// in the navbar Analytics menu.
const DASHBOARDS = {
  breeder: { name: "Main data exploration", hasRole: hasAnalyticsRole },
  pca: { name: "Diversity mapping (PCA)", hasRole: hasPcaRole },
  anova: { name: "Modality characterization (GLM)", hasRole: hasAnovaRole },
};

export const AnalyticsUnavailable = () => {
  const location = useLocation();
  const history = useHistory();
  const requested = new URLSearchParams(location.search).get("from");
  const from = DASHBOARDS[requested] ? requested : "breeder";
  const dashboard = DASHBOARDS[from];

  const dispatch = useDispatch();
  const [
    trial,
    trialDate,
    traitsListForMap,
    refreshingFeaturesByDate,
    featuresByDateFailed,
  ] = useSelector(({ resultMap }) => [
    resultMap.trial,
    resultMap.trial_date,
    resultMap.traitsListForMap,
    resultMap.refreshingFeaturesByDate,
    resultMap.featuresByDateFailed,
  ]);
  const selectedContract = useSelector(selectSelectedTrialContractInfo);

  // Every dashboard reads the results, which the backend serves on
  // view_features only: without it there is nothing to wait for.
  const canSeeResults = hasFeaturesRole(selectedContract?.roles);
  const hasRole = canSeeResults && dashboard.hasRole(selectedContract?.roles);

  const resultDates = useSelector(selectDatesWithResults);
  const datesWithResults = useMemo(
    () =>
      (trial?.trial_dates ?? [])
        .filter((date) => resultDates?.has(date))
        .sort(),
    [resultDates, trial]
  );

  // Back to the requested dashboard once the selected date has results
  if (trial && hasRole && traitsListForMap.length > 0)
    return <Redirect to={`/analytics/${from}`} />;

  const latestDate = datesWithResults.at(-1);
  const others = datesWithResults.length;
  const text = refreshingFeaturesByDate
    ? undefined
    : featuresByDateFailed
    ? "The other dates of this site could not be loaded. Reload the page to try again."
    : latestDate
    ? `Results were delivered for ${others} other date${
        others > 1 ? "s" : ""
      } of this site.`
    : "No results have been delivered for this site yet.";
  const showDate = (date) =>
    dispatch(fetchResultMapFeatures(trial, date, false));

  return (
    <div className="analytics-unavailable">
      <aside className="analytics-unavailable__panel">
        <div className="analytics-unavailable__header">
          <span>
            <i className="fa-solid fa-map-pin" /> Site selection
          </span>
        </div>
        <ControlSection alwaysExpanded />
      </aside>
      <div className="analytics-unavailable__body">
        {!trial ? (
          <EmptyState
            icon="fa-solid fa-map-pin"
            title="Select a site"
            text={`Pick a campaign and a site on the left to open ${dashboard.name}.`}
          />
        ) : !hasRole ? (
          <EmptyState
            icon="fa-solid fa-lock"
            title={`${
              canSeeResults ? dashboard.name : "Results"
            } is not included in this campaign`}
            text="Pick a site from another campaign, or contact your campaign manager."
          >
            {from !== "breeder" &&
              canSeeResults &&
              hasAnalyticsRole(selectedContract?.roles) && (
                <Button
                  color="success"
                  onClick={() => history.push("/analytics/breeder")}
                >
                  <i className="fa-solid fa-dna me-2" />
                  Open {DASHBOARDS.breeder.name}
                </Button>
              )}
          </EmptyState>
        ) : (
          <EmptyState
            icon="fa-regular fa-calendar-xmark"
            title={`No results for ${formatDay(trialDate)}`}
            text={text}
          >
            {refreshingFeaturesByDate ? (
              <InlineSpinner text="Looking for dates with results..." />
            ) : (
              latestDate && (
                <>
                  <Button color="success" onClick={() => showDate(latestDate)}>
                    <i className="fa-solid fa-chart-line me-2" />
                    Show latest results · {formatDay(latestDate)}
                  </Button>
                  <p className="empty-state__text mt-2">
                    Other dates with results
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-1">
                    {datesWithResults.map((date) => (
                      <Badge
                        key={date}
                        className="hiphen-badge elevated clickable"
                        onClick={() => showDate(date)}
                      >
                        {formatDay(date)}
                      </Badge>
                    ))}
                  </div>
                </>
              )
            )}
          </EmptyState>
        )}
      </div>
    </div>
  );
};
